import { ReactNode, useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import { getColors, spacing, typography } from '../tokens';

export interface DataTableColumn {
  id: string;
  label: string;
  sortable?: boolean;
  render?: (row: any) => ReactNode;
}

export interface DataTableServerSidePagination {
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
}

export interface DataTableProps {
  columns: DataTableColumn[];
  data: any[];
  title?: string;
  totalCount?: number;
  searchable?: boolean;
  pagination?: boolean;
  rowsPerPageOptions?: number[];
  labelRowsPerPage?: string;
  onRowClick?: (row: any) => void;
  getRowClassName?: (row: any) => string;
  serverSide?: DataTableServerSidePagination;
  size?: 'small' | 'medium' | undefined;
}

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    container: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${spacing(2)}px ${spacing(3)}px`,
      gap: spacing(2),
    },
    titleWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(1.5),
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '1rem',
      fontWeight: 600,
      color: c.text.primary,
      margin: 0,
    },
    countBadge: {
      background: 'rgba(0, 120, 214, 0.15)',
      color: c.text.link,
      borderRadius: 10,
      padding: '2px 8px',
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.75rem',
      fontWeight: 600,
    },
    searchInput: {
      background: c.background.default,
      color: c.text.primary,
      border: `1px solid ${c.border.default}`,
      borderRadius: 8,
      padding: '6px 12px',
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.85rem',
      outline: 'none',
      minWidth: 180,
      transition: 'border-color 0.15s ease',
      '&:focus': {
        borderColor: c.primary.main,
      },
      '&::placeholder': {
        color: c.text.secondary,
      },
    },
    tableHead: {
      '& th': {
        fontFamily: typography.fontFamilyCorpoSText,
        color: fgAlpha(0.6),
        fontSize: '0.85rem',
        fontWeight: 600,
        borderBottom: `1px solid ${c.border.default}`,
        whiteSpace: 'nowrap' as const,
        backgroundColor: c.background.paper,
      },
    },
    sortLabel: {
      '&.MuiTableSortLabel-root': {
        color: fgAlpha(0.6),
      },
      '&.MuiTableSortLabel-root:hover': {
        color: fgAlpha(0.8),
      },
      '&.Mui-active': {
        color: c.primary.main,
      },
      '& .MuiTableSortLabel-icon': {
        color: `${fgAlpha(0.3)} !important`,
      },
      '&.Mui-active .MuiTableSortLabel-icon': {
        color: `${c.primary.main} !important`,
      },
    },
    bodyRow: {
      transition: 'background 0.1s ease',
      cursor: 'default',
      '&:hover': {
        background: fgAlpha(0.03),
      },
      '& td': {
        fontFamily: typography.fontFamilyCorpoSText,
        color: fgAlpha(0.8),
        fontSize: '0.85rem',
        borderBottom: `1px solid ${c.border.default}`,
      },
      '&:last-child td': {
        borderBottom: 'none',
      },
    },
    clickableRow: {
      cursor: 'pointer',
    },
    pagination: {
      color: fgAlpha(0.6),
      fontFamily: typography.fontFamilyCorpoSText,
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
        {
          color: fgAlpha(0.6),
          fontFamily: typography.fontFamilyCorpoSText,
        },
      '& .MuiTablePagination-select': {
        color: fgAlpha(0.8),
      },
      '& .MuiTablePagination-actions button': {
        color: fgAlpha(0.6),
      },
    },
    empty: {
      textAlign: 'center',
      padding: '48px 24px',
      color: c.text.secondary,
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.9rem',
    },
  };
});

export const DataTable = ({
  columns,
  data,
  title,
  totalCount,
  searchable = false,
  pagination = false,
  rowsPerPageOptions = [10, 25, 50],
  labelRowsPerPage,
  onRowClick,
  getRowClassName,
  serverSide,
  size,
}: Readonly<DataTableProps>) => {
  const { classes } = useStyles();
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0] ?? 25);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleSort = (columnId: string) => {
    const newDirection =
      sortColumn === columnId && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(columnId);
    setSortDirection(newDirection);
    serverSide?.onSortChange?.(columnId, newDirection);
  };

  const processedData = useMemo(() => {
    let result = [...data];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(row =>
        columns.some(col =>
          String(row[col.id] ?? '')
            .toLowerCase()
            .includes(term),
        ),
      );
    }

    if (sortColumn) {
      result.sort((a, b) => {
        const aVal = String(a[sortColumn] ?? '');
        const bVal = String(b[sortColumn] ?? '');
        const cmp = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [data, searchTerm, sortColumn, sortDirection, columns]);

  // When serverSide: data is already the correct page from the server — no slicing needed.
  // Internal sort still applies to current-page rows.
  const slicedData = pagination
    ? processedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : processedData;
  const paginatedData = serverSide ? processedData : slicedData;

  const showHeader = title || searchable;

  return (
    <div className={classes.container}>
      {showHeader && (
        <div className={classes.headerRow}>
          {title && (
            <div className={classes.titleWrapper}>
              <h3 className={classes.title}>{title}</h3>
              {(totalCount !== undefined ||
                (serverSide && serverSide.totalCount >= 0)) && (
                <span className={classes.countBadge}>
                  {serverSide ? serverSide.totalCount : totalCount}
                </span>
              )}
            </div>
          )}
          {searchable && (
            <input
              className={classes.searchInput}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          )}
        </div>
      )}
      <div className={classes.tableScroll}>
        <Table size={size ?? undefined} stickyHeader>
          <TableHead className={classes.tableHead}>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.id}>
                  {col.sortable ? (
                    <TableSortLabel
                      className={classes.sortLabel}
                      active={sortColumn === col.id}
                      direction={sortColumn === col.id ? sortDirection : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className={classes.empty}>
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, idx) => (
                <TableRow
                  key={idx}
                  className={`${classes.bodyRow} ${onRowClick ? classes.clickableRow : ''} ${getRowClassName?.(row) ?? ''}`}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map(col => (
                    <TableCell key={col.id}>
                      {col.render ? col.render(row) : String(row[col.id] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {(pagination || serverSide) && (
        <TablePagination
          className={classes.pagination}
          component="div"
          count={serverSide ? serverSide.totalCount : processedData.length}
          page={serverSide ? serverSide.page : page}
          onPageChange={(_, newPage) =>
            serverSide ? serverSide.onPageChange(newPage) : setPage(newPage)
          }
          rowsPerPage={serverSide ? serverSide.pageSize : rowsPerPage}
          onRowsPerPageChange={e => {
            const n = Number.parseInt(e.target.value, 10);
            if (serverSide) {
              serverSide.onPageSizeChange(n);
            } else {
              setRowsPerPage(n);
              setPage(0);
            }
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          {...(labelRowsPerPage && { labelRowsPerPage })}
        />
      )}
    </div>
  );
};
